import React, { useState, useEffect, useContext } from "react";
import { useSearchParams, Link, Navigate } from "react-router-dom";
import { UserContext } from "../../../components/UserContext";
import ProfilePage from "../ProfilePage";
import WorkspaceBookingsPage from "../WorkspaceBookingsPage";
import AddListingsForm from "../AddListingsForm";
import HostDashboardPage from "./HostDashboardPage";
import HostListingsPage from "./HostListingsPage";

type Section = "dashboard" | "profile" | "listings" | "bookings";

const DashboardIcon = ({ active }: { active: boolean }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill={active ? "currentColor" : "none"}
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className="w-5 h-5 flex-shrink-0"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z"
    />
  </svg>
);

const BuildingIcon = ({ active }: { active: boolean }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill={active ? "currentColor" : "none"}
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className="w-5 h-5 flex-shrink-0"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21"
    />
  </svg>
);

const CalendarIcon = ({ active }: { active: boolean }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill={active ? "currentColor" : "none"}
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className="w-5 h-5 flex-shrink-0"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5M9 12.75h6m-6 3h6"
    />
  </svg>
);

const ProfileIcon = ({ active }: { active: boolean }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill={active ? "currentColor" : "none"}
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className="w-5 h-5 flex-shrink-0"
  >
    <path
      strokeLinecap="round"
      d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z"
    />
  </svg>
);

const PlusIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={2}
    stroke="currentColor"
    className="w-4 h-4 flex-shrink-0"
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
  </svg>
);

const ArrowLeftIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className="w-4 h-4 flex-shrink-0"
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18" />
  </svg>
);

export default function HostPage() {
  const { user, ready } = useContext(UserContext);
  const [searchParams] = useSearchParams();
  const [activeSection, setActiveSection] = useState<Section>("dashboard");
  const [showAddWorkspace, setShowAddWorkspace] = useState(false);

  useEffect(() => {
    const section = searchParams.get("section");
    if (section === "addWorkspace") {
      setShowAddWorkspace(true);
      setActiveSection("listings");
    } else if (
      section === "dashboard" ||
      section === "listings" ||
      section === "bookings" ||
      section === "profile"
    ) {
      setActiveSection(section as Section);
      setShowAddWorkspace(false);
    }
  }, [searchParams]);

  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 rounded-full border-2 border-[var(--secondary)] border-t-transparent animate-spin" />
          <span className="text-sm text-gray-500">Loading your dashboard…</span>
        </div>
      </div>
    );
  }

  if (!user) return <Navigate to="/" />;

  const navItems: { id: Section; label: string; icon: React.JSX.Element }[] = [
    {
      id: "dashboard",
      label: "Dashboard",
      icon: <DashboardIcon active={activeSection === "dashboard"} />,
    },
    {
      id: "listings",
      label: "My Workspaces",
      icon: <BuildingIcon active={activeSection === "listings"} />,
    },
    {
      id: "bookings",
      label: "Bookings",
      icon: <CalendarIcon active={activeSection === "bookings"} />,
    },
    {
      id: "profile",
      label: "Profile",
      icon: <ProfileIcon active={activeSection === "profile"} />,
    },
  ];

  const sectionTitle: Record<Section, string> = {
    dashboard: "Dashboard",
    profile: "Profile",
    listings: "My Workspaces",
    bookings: "Bookings",
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      {/* ── Sidebar ── */}
      {!showAddWorkspace && (
        <aside className="w-64 flex-shrink-0 bg-[var(--secondary)] flex flex-col">
          {/* Brand header */}
          <div className="px-6 pt-6 pb-4 border-b border-white/10">
            <span className="inline-flex items-center gap-1.5 bg-[var(--primary)]/20 text-[var(--primary)] text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full">
              <span className="w-1.5 h-1.5 rounded-full bg-[var(--primary)]" />
              Host Dashboard
            </span>
          </div>

          {/* User card */}
          <div className="px-5 py-5 border-b border-white/10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[var(--primary)] flex items-center justify-center text-[var(--secondary)] font-bold text-sm flex-shrink-0">
                {user.name?.[0]?.toUpperCase() ?? "?"}
              </div>
              <div className="min-w-0">
                <p className="text-white font-semibold text-sm truncate">{user.name}</p>
                <p className="text-white/50 text-xs truncate">{user.email}</p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 space-y-0.5">
            <p className="text-white/30 text-[10px] uppercase tracking-widest font-semibold px-3 pb-3">
              Manage
            </p>
            {navItems.map(({ id, label, icon }) => {
              const isActive = activeSection === id;
              return (
                <button
                  key={id}
                  onClick={() => setActiveSection(id)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors text-left ${
                    isActive
                      ? "bg-white/15 text-white border-l-2 border-[var(--primary)] pl-[10px]"
                      : "text-white/70 hover:bg-white/8 hover:text-white"
                  }`}
                >
                  {icon}
                  {label}
                </button>
              );
            })}

            {/* Add workspace CTA — only shown on listings section */}
            {activeSection === "listings" && (
              <div className="pt-4">
                <button
                  onClick={() => setShowAddWorkspace(true)}
                  className="w-full flex items-center gap-2 px-3 py-2.5 bg-[var(--primary)] text-[var(--secondary)] rounded-xl text-sm font-semibold transition-opacity hover:opacity-90"
                >
                  <PlusIcon />
                  Add Workspace
                </button>
              </div>
            )}
          </nav>

          {/* Footer */}
          <div className="px-3 py-4 border-t border-white/10">
            <Link
              to="/account?section=profile"
              className="flex items-center gap-2 px-3 py-2.5 text-white/50 hover:text-white hover:bg-white/5 rounded-xl text-sm transition-colors"
            >
              <ArrowLeftIcon />
              Switch to renting
            </Link>
          </div>
        </aside>
      )}

      {/* ── Content ── */}
      <main className="flex-1 min-w-0">
        {showAddWorkspace ? (
          <AddListingsForm
            onBack={() => setShowAddWorkspace(false)}
            onSuccess={() => {
              setShowAddWorkspace(false);
              setActiveSection("listings");
            }}
          />
        ) : (
          <div className="min-h-full">
            {/* Content area header */}
            <div className="bg-white border-b border-gray-200 px-8 py-5">
              <h1 className="text-xl font-semibold text-gray-900">
                {sectionTitle[activeSection]}
              </h1>
            </div>

            <div className="p-0">
              {activeSection === "dashboard" && (
                <HostDashboardPage onNavigate={(s) => setActiveSection(s as Section)} />
              )}
              {activeSection === "profile" && <ProfilePage />}
              {activeSection === "listings" && (
                <HostListingsPage onManage={() => setActiveSection("bookings")} />
              )}
              {activeSection === "bookings" && <WorkspaceBookingsPage />}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
